const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const EXPIRES_AT_KEY = "expiresAt";
const COOKIE_CONSENT_KEY = "cookieConsent";

/**
 * Token storage backed by localStorage.
 *
 * Replaces the previous cookie-based storage (js-cookie). Tokens are no longer
 * kept in browser cookies, so they are never auto-attached to requests — the
 * access token is sent ONLY via the `Authorization: Bearer` header (see
 * auth.ky.hooks.ts). This removes the redundant cookie transmission and the
 * associated CSRF footgun described in docs/ACCESS_TOKEKS_RESEARCHED.md.
 *
 * The public surface is intentionally identical to the old `cookieService`
 * (including `clearAuthCookies`) to keep call sites unchanged.
 */

const getItem = (key: string): string | undefined => {
  try {
    return localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const setItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures (e.g. private mode quota) — auth still works in-session.
  }
};

const removeItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // no-op
  }
};

export const tokenStorage = {
  setAccessToken: (token: string) => {
    setItem(ACCESS_TOKEN_KEY, token);
  },
  getAccessToken: (): string | undefined => {
    return getItem(ACCESS_TOKEN_KEY);
  },
  removeAccessToken: () => {
    removeItem(ACCESS_TOKEN_KEY);
  },
  setRefreshToken: (token: string) => {
    setItem(REFRESH_TOKEN_KEY, token);
  },
  getRefreshToken: (): string | undefined => {
    return getItem(REFRESH_TOKEN_KEY);
  },
  removeRefreshToken: () => {
    removeItem(REFRESH_TOKEN_KEY);
  },
  setExpiresAt: (timestamp: number) => {
    setItem(EXPIRES_AT_KEY, String(timestamp));
  },
  getExpiresAt: (): number | undefined => {
    const expiresAt = getItem(EXPIRES_AT_KEY);
    return expiresAt ? Number(expiresAt) : undefined;
  },
  removeExpiresAt: () => {
    removeItem(EXPIRES_AT_KEY);
  },
  isAccessTokenValid: () => {
    const token = tokenStorage.getAccessToken();
    if (!token) return false;
    const expiresAt = tokenStorage.getExpiresAt();
    if (!expiresAt) return true; // no expiry stored — assume valid
    return Date.now() < expiresAt;
  },
  clearAuthCookies: () => {
    tokenStorage.removeAccessToken();
    tokenStorage.removeRefreshToken();
    tokenStorage.removeExpiresAt();
  },
  getCookieConsent: (): boolean | null => {
    const consent = getItem(COOKIE_CONSENT_KEY);
    if (consent === undefined) return null;
    return consent === "accepted";
  },
  setCookieConsent: (accepted: boolean) => {
    setItem(COOKIE_CONSENT_KEY, accepted ? "accepted" : "declined");
  },
};

/**
 * One-shot best-effort removal of legacy auth cookies set by the previous
 * (js-cookie) implementation, so returning users don't keep stale tokens in
 * their cookie jar after upgrading. Safe to call on every startup; it only
 * expires cookies that may still exist. Remove in a later release.
 */
export const clearLegacyAuthCookies = (): void => {
  if (typeof document === "undefined") return;
  const expiry = "=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  for (const key of [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, EXPIRES_AT_KEY]) {
    document.cookie = `${key}${expiry}`;
  }
};
